/**
 * CellRevealLogo — React component
 * Brand mark + wordmark. Palette: Dark Teal + Cream.
 * Requires Plus Jakarta Sans (already loaded via globals.css).
 *
 * Usage:
 *   <CellRevealLogo />                        // lockup, xl, light
 *   <CellRevealLogo variant="dark" />         // dark background
 *   <CellRevealLogo variant="cream" />        // cream/tinted background
 *   <CellRevealLogo type="icon" size="sm" />  // icon only
 *   <CellRevealLogo animated />               // staggered draw-in on mount
 */

import { useEffect, useId, useState } from "react";

const ARCS = [
  { r: 7,  dash: "32.99 10.99",   offset:  10.99 },
  { r: 13, dash: "61.26 20.42",   offset:  13.61 },
  { r: 19, dash: "89.54 29.85",   offset:   9.95 },
  { r: 25, dash: "117.81 39.27",  offset:   0.00 },
  { r: 31, dash: "146.09 48.69",  offset: -16.23 },
];

const ORBIT_DURATIONS = [32, 22, 16, 11, 7];
const ORBIT_DIRS = ["normal", "reverse", "normal", "reverse", "normal"];

const FULL_CIRCS = [43.98, 81.68, 119.38, 157.08, 194.78];

const ARC_GRADIENTS: [string, string][] = [
  ["#052e16", "#115e59"],
  ["#115e59", "#0f766e"],
  ["#0f766e", "#14b8a6"],
  ["#14b8a6", "#d4a574"],
  ["#d4a574", "#fef3c7"],
];

const ARC_GRADIENTS_CREAM: [string, string][] = [
  ["#052e16", "#115e59"],
  ["#115e59", "#0f766e"],
  ["#0f766e", "#14b8a6"],
  ["#14b8a6", "#92400e"],
  ["#a16207", "#78350f"],
];

const WORDMARK = {
  light: { cell: "#115e59", reveal: "#0f766e" },
  cream: { cell: "#115e59", reveal: "#0f766e" },
  dark:  { cell: "#ccfbf1", reveal: "#fef3c7" },
};

const SIZES = {
  xs:  { icon: 16, gap:  6, font: 11, strokeWidth: 5.0, glow: 0.8 },
  sm:  { icon: 22, gap:  8, font: 13, strokeWidth: 3.5, glow: 1.0 },
  md:  { icon: 32, gap: 10, font: 18, strokeWidth: 2.8, glow: 1.2 },
  lg:  { icon: 44, gap: 14, font: 24, strokeWidth: 2.4, glow: 1.4 },
  xl:  { icon: 64, gap: 20, font: 34, strokeWidth: 2.2, glow: 1.6 },
  "2xl":{ icon: 88, gap: 24, font: 46, strokeWidth: 2.2, glow: 1.8 },
};

type Variant = "light" | "dark" | "cream";
type Size = keyof typeof SIZES;

interface IconProps {
  variant?: Variant;
  size?: Size;
  animated?: boolean;
  orbit?: boolean;
  pulseGlow?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

interface LogoProps extends IconProps {
  type?: "lockup" | "icon";
}

export function CellRevealIcon({
  variant = "light",
  size = "md",
  animated = false,
  orbit = false,
  pulseGlow = false,
  className,
  style,
  ariaLabel = "CellReveal",
}: IconProps) {
  const uid = useId().replace(/:/g, "");
  const cfg = SIZES[size] ?? SIZES.md;
  const gradients = variant === "cream" ? ARC_GRADIENTS_CREAM : ARC_GRADIENTS;
  const blur = (variant === "dark" ? 1.8 : 1.2) * (cfg.glow / 1.6);

  const [revealed, setRevealed] = useState(!animated);
  useEffect(() => {
    if (!animated) return;
    const t = setTimeout(() => setRevealed(true), 80);
    return () => clearTimeout(t);
  }, [animated]);

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox="0 0 64 64"
      width={cfg.icon}
      height={cfg.icon}
      overflow="visible"
      className={`${pulseGlow ? "animate-logo-pulse" : ""} ${className ?? ""}`.trim() || undefined}
      style={{ flexShrink: 0, ...style }}
    >
      <defs>
        {gradients.map((g, i) => (
          <linearGradient
            key={i}
            id={`${uid}-g${i}`}
            gradientUnits="userSpaceOnUse"
            x1={32 - ARCS[i].r} y1={32 - ARCS[i].r}
            x2={32 + ARCS[i].r} y2={32 + ARCS[i].r}
          >
            <stop offset="0%" stopColor={g[0]} />
            <stop offset="100%" stopColor={g[1]} />
          </linearGradient>
        ))}
        <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation={blur} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {ARCS.map((a, i) => {
        const fullC = FULL_CIRCS[i];
        const dash  = animated && !revealed ? `${fullC} 0` : a.dash;
        const off   = animated && !revealed ? fullC : a.offset;

        const circle = (
          <circle
            cx="32" cy="32" r={a.r}
            fill="none"
            stroke={`url(#${uid}-g${i})`}
            strokeWidth={cfg.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={orbit ? a.dash : dash}
            strokeDashoffset={orbit ? a.offset : off}
            filter={`url(#${uid}-glow)`}
            style={animated && !orbit ? {
              opacity: !revealed ? 0 : 1,
              transition: `stroke-dashoffset 0.75s cubic-bezier(0.16,1,0.3,1) ${i * 0.13}s, opacity 0.4s ease ${i * 0.13}s`,
            } : {}}
          />
        );

        if (orbit) {
          return (
            <g
              key={i}
              style={{
                animation: `orbit-spin ${ORBIT_DURATIONS[i]}s linear ${ORBIT_DIRS[i]} infinite`,
                transformOrigin: "32px 32px",
              }}
            >
              {circle}
            </g>
          );
        }

        return <g key={i}>{circle}</g>;
      })}
    </svg>
  );
}

export default function CellRevealLogo({
  variant = "light",
  size = "xl",
  type = "lockup",
  animated = false,
  orbit = false,
  pulseGlow = false,
  className,
  style,
  ariaLabel = "CellReveal",
}: LogoProps) {
  const cfg = SIZES[size] ?? SIZES.xl;
  const colors = WORDMARK[variant] ?? WORDMARK.light;

  if (type === "icon") {
    return (
      <CellRevealIcon
        variant={variant}
        size={size}
        animated={animated}
        orbit={orbit}
        pulseGlow={pulseGlow}
        className={className}
        style={style}
        ariaLabel={ariaLabel}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: cfg.gap,
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        lineHeight: 1,
        userSelect: "none",
        ...style,
      }}
    >
      <CellRevealIcon variant={variant} size={size} animated={animated} ariaLabel="" />
      <span style={{ display: "inline-flex", alignItems: "baseline" }}>
        <span style={{ fontWeight: 300, fontSize: cfg.font, letterSpacing: "-0.022em", color: colors.cell }}>
          Cell
        </span>
        <span style={{ fontWeight: 700, fontSize: cfg.font, letterSpacing: "-0.042em", color: colors.reveal }}>
          Reveal
        </span>
      </span>
    </span>
  );
}
